import { useEffect, useRef, useState } from "react";
import { GetData } from "../Api";
import moment from 'moment';
import { FAUST_URL } from "../Api/Constants";
import {isEmpty, groupBy, sumBy, sortBy, last } from 'lodash';
import DateUtil from "../utils/date-util.service.js";
import PnPUtilService from '../utils/pnp-util.service.js';
import CustomDonutChart from "../Components/DonutChart/CustomDonutChart";
import WidgetLoader from "../Components/loaders/widget-loader.component";
import { useTranslation } from "react-i18next";
moment.suppressDeprecationWarnings = true;

const PnpMachineStatus = (props)=>{
  const { t } = useTranslation();
  let [lastEventDataObject, setLastEventDataObject] = useState({});
  let [lastEventLoading, setLastEventLoading] = useState(true)
  const colorCodes = PnPUtilService.getColorCodes();
  let isMachineChanged = useRef(false);
  let [loading, setLoading] = useState(true);
  let donutData = useRef([]);
  let lastTimeOut = useRef('');
  let dataRefreshTimer = useRef(null);
  let machineStatusEventStream = useRef(null);

  const refreshData = (start, end) => {
    destroyDataRefresh();
    let startTs = moment(start ?? props.from).unix();
    let endTs = moment(end ?? props.to).unix();
    getMachineStatusData(startTs, endTs);
    dataRefreshTimer.current = setTimeout(()=>{
      let diff = 0;
      if(props.rangeSelected){
        diff = 3*60*1000; // XXX -> 3 mins x 60 seconds x 1000 milliseconds
      }
      refreshData((props.from+diff), (props.to+diff));
    }, PnPUtilService.PNP_MACHINE_STATUS_REFRESH);
  };
  const destroyDataRefresh = ()=>{
    if(dataRefreshTimer.current){
      clearTimeout(dataRefreshTimer.current);
    }
  };
  const identifyLastEventData = (data) => {
    if(!isEmpty(data)){
      const temp = last(sortBy(data,'ts'));
      if( isEmpty(lastEventDataObject)
        || isMachineChanged.current || (lastEventDataObject.ts)){
        isMachineChanged.current = false;
        setLastEventDataObject(temp);
        setLastEventLoading(true);
      }
    } else {
      clearLastMachineStatus();
    }
  };
  const getLastStatus = ()=>{
    lastTimeOut.current = '';
    if(!isEmpty(lastEventDataObject) && lastEventDataObject?.ts){
      lastTimeOut.current = DateUtil.getTimeSince(lastEventDataObject.ts);
    }
    setLastEventLoading(false);
  };
  const clearLastMachineStatus= ()=>{
    setLastEventDataObject({});
    lastTimeOut.current = '';
    setLastEventLoading(false);
  };
  const listenOnStreamChanges = () => { 
    destroyStream();
    setLoading(true);
    if(!isEmpty(props.selectedMachineName)){ 
      let temp = new EventSource(`${FAUST_URL}stream?machine=${props.selectedMachineName}&report=mcstatus`); 
      temp.onerror = () => {
        setLoading(false);
      };
      temp.onopen = () => {
        setLoading(false);
      };
      temp.onmessage = (e) => {
        let serverSideEventData = JSON.parse(e.data);
        if(!isEmpty(serverSideEventData)){
          identifyLastEventData([serverSideEventData]);
        }
      };
      machineStatusEventStream.current = temp;
    }
  };
  const destroyStream = ()=>{
    if (machineStatusEventStream.current) {
      machineStatusEventStream.current.close();
    }
  };
  const drawMachineStatusWithData = (startTs, endTs, dataToRender) => {
    const startMilliseconds = startTs*1000;
    const endMilliseconds = endTs*1000;
    if(!isEmpty(dataToRender)){
      let newData = [];
      dataToRender.forEach((item, index)=>{
        if(index === 0){
          let diff = item.ts - startMilliseconds;
          if(startMilliseconds === item.ts){
            if(dataToRender.length === 1){
              diff = endMilliseconds - item.ts;
              newData.push({ status: item.status, duration : diff });
            } else {
              diff = dataToRender[index+1].ts - dataToRender[index].ts;
              newData.push({ status: item.status, duration : diff });
            }
          }else  if(startMilliseconds < item.ts){
            if(!isEmpty(item.prev_status)){
              newData.push({ status: item.prev_status, duration: diff });
            }
            if(dataToRender.length === 1){
              diff = endMilliseconds - item.ts;
              newData.push({ status: item.status, duration : diff });
            } else {
              diff = dataToRender[index+1].ts - dataToRender[index].ts;
              newData.push({ status: item.status, duration : diff });
            }
          }
        } else  if(index === dataToRender.length-1){
          const diff = endMilliseconds - item.ts;
          newData.push({ status: item.status, duration: diff });
        } else {
          const diff = dataToRender[index+1].ts - dataToRender[index].ts;
          newData.push({ status: item.status, duration : diff });
        }
      });
      let dataByStatus = groupBy(newData, 'status');
      let labels = [], dataPointes = [], colors = [];
      for(const key in dataByStatus){
        if(dataByStatus.hasOwnProperty(key)){
          const sum = sumBy(dataByStatus[key], 'duration');
          dataPointes.push(sum);
          colors.push(colorCodes[key.toUpperCase()]);
          const value = (sum / (endMilliseconds-startMilliseconds)) * 100;
          labels.push(`${key} : ${parseFloat(value.toFixed(2))}%`);
        }
      }
      donutData.current = [dataPointes,labels,colors];
    }else{
      donutData.current = [];
    }
    setLoading(false);
  };
  const getMachineStatusData = (startTs, endTs) => {
    setLoading(true);
    GetData(`dashboard/pick-place?PNP=MACHINE_STATUS&factoryId=${props.factory.factoryId}&startTs=${startTs}&endTs=${endTs}&machineId=${props.selectedMachineName}&reportType=mcstatus`)
      .then((response) => {
        let data = response?.data;
        if(isEmpty(data)){
          drawMachineStatusWithData(startTs, endTs, []);
        } else {
          drawMachineStatusWithData(startTs, endTs, data);
        }
      })
      .catch(() => {
        drawMachineStatusWithData(startTs, endTs, []);
      });
  };
  
  const tooltipFormatter =  ({series, seriesIndex, dataPointIndex, w}) =>{
    return `<div style='padding:5px;background-color:
              ${w.config.colors[seriesIndex]}'>${w.config.labels[seriesIndex]}
              </div>`;
  };
  useEffect(()=>{
    if(!isEmpty(props.factory) 
    && !isEmpty(props.selectedMachineName) 
    && !isNaN(props.from) 
    && !isNaN(props.to)){
      refreshData();
      listenOnStreamChanges();
    }
    return ()=>{
      destroyStream();
      destroyDataRefresh();
      clearLastMachineStatus();
    };
  }, [props]);

  useEffect(()=>{
    getLastStatus();
  },[lastEventDataObject]);

  return (
    <>
      <div className="cv-pnp-machine-status">
        <div className="px-4 pt-2">
          <div className="d-flex flex-row justify-content-between">
            <div className="inspection-dashboard-title">
              {props.selectedMachineName} - {t("LT_machine_status")}
            </div>
          </div>
          <div className="d-flex">
              <div className="cv-wait-switch rounded-circle" style={{background: colorCodes[lastEventDataObject?.status?.toUpperCase()]}}>&nbsp;</div>
              <div>{lastEventDataObject?.status}</div>
              <div>
                &nbsp;&nbsp;{lastTimeOut.current}
                {lastEventLoading ? <span className="fa fa-circle-o-notch fa-spin fa-fw"></span>:""}
              </div>
            </div>
        </div>
        <div className="d-flex justify-content-center">
          {
            loading ?
            <div className="cv-machine-status-loader d-flex justify-content-center align-items-center">
              <WidgetLoader /> 
            </div> :
            <CustomDonutChart series= {donutData.current[0]} labels={donutData.current[1]} 
              legendPosition="left" width="430" fillColors = {donutData.current[2]} customTooltipFormatter = {tooltipFormatter}/>
          }
        </div>
      </div>
    </>
  );
}
export default PnpMachineStatus;
